import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddCircle,
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  House,
  Output,
  PeopleAlt,
  Place,
  Share,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { AudienceSkeleton } from "./AudienceSkeleton";
import {
  buildAudience,
  createNotification,
  getInsight,
  saveAudience,
  shareAudience,
  updateAudienceName,
  updateAudienceStatus
} from "@/api";
import { PageContainer } from "@/components/containers";
import {
  BuildModeSelect,
  ErrorPanel,
  HelpBox,
  HelpText,
  LocationSelection,
  QueryGroup,
  QueryGroupOperator,
  StatBox,
  useActivePage,
} from "@/components/atoms";
import { AudienceMiniStats } from "@/components/molecules";
import { useAudienceContext, useDialogs, useSnackbar } from "@/context";
import { useAudience, useLocations, useVariables } from "@/hooks";
import { Breadcrumb, GeoFenceLocation } from "@/types";
import {
  abbreviateNumber,
  checkAudienceSize,
  checkVariables,
  getAudienceBreadcrumb,
  modeThreshold,
} from "@/utils";

const AudiencePage: React.FC = () => {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { state, dispatch } = useAudienceContext();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialogs();

  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience,
    loading: audienceLoading,
    error: audienceError,
  } = useAudience(audienceId, "build");
  const numQueryGroups = audience?.var_groups?.length || 0;
  const audienceSharedToUser = audience?.shared;
  const activePage = useActivePage();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const { openSnackbar } = useSnackbar();
  const {
    variables,
    loading: variablesLoading,
    error: variablesError,
  } = useVariables();
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
  } = useLocations();
  const pageLoading = audienceLoading || variablesLoading || locationsLoading;
  const processingStatus = audience && audience.status === 'processing';
  const errorStatus = audience && audience.status === 'error';
  const readOnly = audience?.shared || processingStatus;

  const [buildLoading, setBuildLoading] = useState<boolean>(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState<boolean>(false);
  const [insightError, setInsightError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [invalidVariables, setInvalidVariables] = useState<boolean>(false);

  useEffect(() => {
    setBreadcrumbs(
      getAudienceBreadcrumb(activePage, audience, audienceLoading)
    );
  }, [activePage, audience, audienceLoading]);

  useEffect(() => {
    if (audience && !checkVariables(audience, variables) && !audienceSharedToUser) {
      setInvalidVariables(true);
    } else {
      setInvalidVariables(false);
    }
  }, [audience, variables]);

  useEffect(() => {
    state.change && setBuildError(null);
  }, [state.change]);

  const handleAddGroup = () => {
    dispatch({ type: "ADD_VARIABLE_GROUP" });
  };

  const handleChangeOperator = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "CHANGE_OPERATOR", payload: event.target.value });
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "CHANGE_BUILD_MODE", payload: event.target.value });
  };

  const handleSelectLocation = (locations: GeoFenceLocation[]) => {
    dispatch({ type: "SET_GEO_FENCE", payload: locations });
  };

  const handleBuildAudience = async () => {
    const token = await getAccessTokenSilently();
    setBuildError(null);
    setBuildLoading(true);

    if (audience) {
      const queries = audience.var_groups
        .filter((group) => group.variables.length > 0)
        .map((group) => {
          const ids = group.variables.map((v) => v.tag_id).join(",");
          return `${group.operator}(${ids})`;
        });

      const geoFence =
        audience.geo_fence && audience.geo_fence.map((x) => x.geo_id).join(",");

      const { data, error } = await buildAudience(
        token,
        queries,
        modeThreshold[audience.build_mode],
        audience.operator,
        geoFence,
        audienceId
      );

      if (data) {
        if (checkAudienceSize(data.audience_size[0])) {
          dispatch({ type: "SET_STATUS", payload: "idle" });
          dispatch({ type: "BUILD_AUDIENCE", payload: data });
        } else {
          setBuildError(
            "Unable to build audience or the audience size was 0, please adjust your criteria and try again"
          );
        }
      }

      if (error) {
        setBuildError(error.message);
      }
    }

    setBuildLoading(false);
  };

  const handleAudienceInsight = () => {
    if (!audience) return;

    if (audience.audience_id && audience.audience_id !== "") {
      // Existing audience - if change show warning and rebuild otherwise go to insights
      if (state.change) {
        openDialog({
          type: "confirm",
          props: {
            message: `\nProceeding will mean that your saved audience will get overwritten.\nCreate a new audience if you don't want this to happen.\n`,
            onConfirm: async () => {
              await handleUpdateAudience(
                audience.audience_name,
                audience.project_name
              );
            },
          },
        });
      } else {
        navigate("insight");
      }
    } else {
      // New audience - show save audience dialog then go to insights
      openDialog({
        type: "createAudience",
        props: {
          onConfirm: handleUpdateAudience,
        },
      });
    }
  };

  const handleAudienceExport = () => {
    navigate("export");
  };

  const handleUpdateAudience = async (
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();
    closeDialog();
    setInsightError(null);
    setInsightLoading(true);

    if (audience) {

      // Prepare the audience data to save
      let newAudienceName = audienceName ?? audience.audience_name;
      let newProjectName = projectName ?? audience.project_name;
      let updatedAudience = {
        ...audience,
        audience_name: newAudienceName,
        project_name: newProjectName,
      };

      // Save the audience - this builds the audience tables too
      const { data, error } = await saveAudience(token, updatedAudience, true);

      if (data) {

        // Extract the audience from the response
        const newAudience = data.filter(
          (audience) => audience.audience_name === updatedAudience.audience_name
        )[0];
        // newAudience.status = "processing";  // TODO Removing this until we do status properly
        dispatch({
          type: "UPDATE_AUDIENCE",
          payload: { audiences: data, currentAudience: newAudience },
        });

        // Calculate insight
        const insight = await getInsight(token, newAudience.audience_id, true, newAudience.client);

        if (insight.data) {

          // Update the context with the insight payload
          dispatch({ type: "SET_STATUS", payload: "complete" });
          dispatch({ type: "CREATE_INSIGHT", payload: { ...insight.data } });

          const notification = {
            client: newAudience.client,
            message: `Audience "${newAudience.audience_name}" successfully created`,
            category: "Audience",
            data: { audience_id: newAudience.audience_id}
          }
          await createNotification(token, notification).then(() =>
            navigate(`../${newAudience.audience_id}/insight`, {
              relative: "path",
            })
          );

          // Update the audience status in the database
          const status = await updateAudienceStatus(
            token,
            audienceId,
            'complete'
          );

          if (status.error) {
            setInsightError(status.error.message);
          }
        }

        if (insight.error) {
          dispatch({ type: "SET_STATUS", payload: "error" });
          setInsightError(insight.error.message);
        }
      }

      if (error) {
        dispatch({ type: "SET_STATUS", payload: "error" });
        setInsightError(error.message);
      }
    }

    setInsightLoading(false);
  };

  const handleUpdateAudienceName = async (
    audienceId: string,
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();

    const { data, error } = await updateAudienceName(
      token,
      audienceId,
      audienceName,
      projectName
    );

    if (data) {
      openSnackbar("Audience name updated successfully", "success");
      dispatch({
        type: "CHANGE_NAME",
        payload: { audienceId, audienceName, projectName },
      });
      closeDialog();
    }

    if (error) {
      openSnackbar(error.message, "error");
      closeDialog();
      setUpdateError(error.message);
    }
  };

  const handleShareAudience = async (ids: number[]) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await shareAudience(
      accessToken,
      audience.audience_id,
      ids
    );

    if (data) {
      openSnackbar(data.message, "success");
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      openSnackbar(error.message, "error");
      setUpdateError(error.message);
      closeDialog();
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        color="error"
        disableElevation
        startIcon={<Delete />}
        disabled={
          pageLoading ||
          buildLoading ||
          insightLoading ||
          audienceId === undefined ||
          audienceSharedToUser ||
          processingStatus
        }
        onClick={() =>
          openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/audience",
            },
          })
        }
      >
        Delete
      </Button>
      {!audienceSharedToUser && (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Share />}
          disabled={
            pageLoading ||
            buildLoading ||
            insightLoading ||
            audienceId === undefined ||
            processingStatus
          }
          onClick={() =>
            openDialog({
              type: "shareAudience",
              props: {
                selectedEntity: audience,
                onShare: async (ids: number[]) => {
                  await handleShareAudience(ids);
                },
              },
            })
          }
        >
          Share
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Construction />}
        loading={buildLoading}
        disabled={
          audience?.var_groups[0].variables.length === 0 ||
          insightLoading ||
          buildLoading ||
          audienceSharedToUser ||
          invalidVariables ||
          processingStatus
        }
        onClick={handleBuildAudience}
      >
        {state.built ? "Re-Build" : "Build"}
      </Button>
      {audience && audience.audience_size && (
        <>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<BarChartOutlined />}
            loading={insightLoading || processingStatus}
            disabled={!state.built || errorStatus}
            onClick={handleAudienceInsight}
          >
            Insight
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<Output />}
            disabled={!state.saved || insightLoading || buildLoading || processingStatus || errorStatus}
            onClick={handleAudienceExport}
          >
            Export
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <PageContainer
      breadcrumbs={audienceError ? undefined : breadcrumbs}
      actions={audienceError ? undefined : actionButtons}
      loading={buildLoading || insightLoading}
      loadingText={
        buildLoading ? "Building Audience" : "Calculating Audience Insight"
      }
    >
      {pageLoading ? (
        <AudienceSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid container size={12}>
            <ErrorPanel
              audienceError={audienceError}
              audienceUpdateError={updateError}
              buildError={buildError}
              insightError={insightError}
              variablesError={variablesError || locationsError}
              invalidVariablesError={invalidVariables}
            />
          </Grid>
          {audience && variables && locations && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !audienceSharedToUser && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openDialog({
                            type: "editAudience",
                            props: {
                              audienceId: audienceId,
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceId: string,
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await handleUpdateAudienceName(
                                  audienceId,
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats audienceSize={audience.audience_size[0]} />
                )}
              </Grid>
              {audience?.shared && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    You are viewing an audience that has been shared with you
                    and therefore any edit functionality has been disabled. You
                    may still view the insights for and export this audience. We
                    will soon be adding the ability to clone this audience so
                    that you can make changes.
                  </Alert>
                </Grid>
              )}
              {processingStatus && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    This audience is still processing, please wait. You will receive
                    a notification when the insights are complete.
                  </Alert>
                </Grid>
              )}
              {errorStatus && (
                <Grid size={12}>
                  <Alert variant="filled" severity="error">
                    There was an error creating the Audience insights. Please rebuild and try again.
                  </Alert>
                </Grid>
              )}
              <Grid size={12}>
                <HelpText
                  text="Audience Query Groups"
                  helpText="
                  A query group is a set of variables that are combined using a boolean operator (e.g. AND/OR) to define an
                  audience behaviour
                  "
                  variant="body1"
                />
              </Grid>
              {audience.var_groups.map((group, index) => (
                <React.Fragment key={index}>
                  <QueryGroup
                    id={group.id}
                    variables={variables}
                    disabled={readOnly}
                  />
                  {numQueryGroups > 1 && index + 1 !== numQueryGroups && (
                    <Grid size={12}>
                      <QueryGroupOperator
                        value={audience.operator}
                        onChange={handleChangeOperator}
                        disabled={readOnly}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
              {!readOnly && (
                <Grid size={12}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    border={1}
                    borderRadius={1}
                    borderColor={theme.palette.divider}
                    p={1}
                  >
                    <HelpBox title="Add Query Group">
                      <IconButton size="small" onClick={handleAddGroup}>
                        <AddCircle color="secondary" />
                      </IconButton>
                    </HelpBox>
                  </Box>
                </Grid>
              )}
              <Grid size={12} mt={2}>
                <HelpText
                  text="Audience Settings"
                  helpText="Additional settings to configure your audience further"
                  variant="body1"
                />
              </Grid>
              <Grid>
                <BuildModeSelect
                  value={audience.build_mode}
                  onChange={handleChangeMode}
                  disabled={readOnly}
                />
              </Grid>
              <Grid>
                <LocationSelection
                  locations={locations}
                  selection={audience.geo_fence}
                  onSelect={handleSelectLocation}
                  disabled={readOnly}
                />
              </Grid>
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="Understand the reach of your audience"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size[0].num_postcodes && (
                    <Grid>
                      <StatBox
                        name="Postcodes"
                        value={audience.audience_size[0].num_postcodes}
                        valueFormatter={abbreviateNumber}
                        icon={<Place fontSize="small" color="secondary" />}
                        disabled={readOnly}
                      />
                    </Grid>
                  )}
                  <Grid>
                    <StatBox
                      name="Households"
                      value={audience.audience_size[0].num_households}
                      valueFormatter={abbreviateNumber}
                      icon={<House fontSize="small" color="secondary" />}
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid>
                    <StatBox
                      name="Audience Size"
                      value={audience.audience_size[0].population_count}
                      valueFormatter={abbreviateNumber}
                      icon={<PeopleAlt fontSize="small" color="secondary" />}
                      disabled={readOnly}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default AudiencePage;
